// ** React Imports

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// ** MUI Components
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
// ** Components
import NotAuthorized from 'src/@core/components/NotAuthorized';
import PleaseLogin from 'src/@core/components/PleaseLogin';
// ** Layout Import
import BlankLayout from 'src/@core/layouts/BlankLayout';

// ** ACL Imports
import { buildAbilityFor } from 'src/configs/acl';
// ** Hooks
import { useAuth } from 'src/hooks/useAuth';
import { AbilityContext } from 'src/layouts/components/acl/Can';
// ** Utils
import getHomeRoute from 'src/layouts/components/acl/getHomeRoute';

// ** Styled Components
const ContentWrapper = styled('div')({
  display: 'flex',
  minHeight: '100vh',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px',
});

const FallbackSpinner = () => {
  return (
    <ContentWrapper>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <CircularProgress sx={{ mb: 4 }} />
        <Typography>Loading...</Typography>
      </Box>
    </ContentWrapper>
  );
};

/**
 * UnifiedGuard Component
 *
 * A single guard component that handles:
 * - Authentication (ensuring users are logged in)
 * - Guest-only pages (redirecting authenticated users)
 * - Role-based access control (ACL)
 *
 * @param {ReactNode} children - The component to render if all guards pass
 * @param {Object} aclAbilities - ACL configuration for the page
 * @param {boolean} guestGuard - Whether this is a guest-only page
 * @param {boolean} authGuard - Whether authentication is required
 */
const UnifiedGuard = (props) => {
  const {
    children,
    aclAbilities,
    guestGuard = false,
    authGuard = true,
  } = props;

  // Ensure authGuard is strictly boolean
  const isAuthRequired = authGuard === true;
  const isGuestOnly = guestGuard === true;

  // ** Hooks
  const auth = useAuth();
  const router = useRouter();

  // ** Vars
  const user = auth.user;
  const isInitialized = auth.isInitialized;

  useEffect(() => {
    // Only need to handle guest-only pages (login, register, etc.)
    // This is the only case where we redirect - when authenticated users try to access guest-only pages
    if (isInitialized && isGuestOnly && user) {
      const homeRoute = getHomeRoute(user.role);
      router.replace(homeRoute);
    }
  }, [isInitialized, router, user, isGuestOnly]);

  // Show loading spinner while auth is initializing
  // For public pages (no auth required), skip the loading state entirely
  if (!isInitialized && (isAuthRequired || isGuestOnly)) {
    return <FallbackSpinner />;
  }

  // For guest pages, always render children (even while redirecting)
  if (isGuestOnly) {
    return <>{children}</>;
  }

  // For public pages (no auth required), just render with ability context
  // This should be checked BEFORE any auth checks to ensure public pages always render
  if (!(isAuthRequired || isGuestOnly)) {
    const ability = buildAbilityFor(user?.role || 'public', aclAbilities);

    return (
      <AbilityContext.Provider value={ability}>
        {children}
      </AbilityContext.Provider>
    );
  }

  // For authenticated pages, check if user exists
  if (isAuthRequired && user) {
    const ability = buildAbilityFor(user.role, aclAbilities);

    // Note: We no longer automatically redirect from the home page
    // Let the home page component handle its own logic

    // Check ACL permissions if specified
    if (aclAbilities?.action && aclAbilities.subject) {
      const canAccess = ability.can(aclAbilities.action, aclAbilities.subject);

      if (!canAccess) {
        return (
          <BlankLayout>
            <NotAuthorized />
          </BlankLayout>
        );
      }
    }

    // User is authenticated and authorized
    return (
      <AbilityContext.Provider value={ability}>
        {children}
      </AbilityContext.Provider>
    );
  }

  // For authenticated pages without a user, show login prompt
  // Users can click login manually to authenticate
  if (isAuthRequired && !user) {
    return (
      <BlankLayout>
        <PleaseLogin />
      </BlankLayout>
    );
  }

  // Default case for public pages while auth is initializing
  // This ensures public pages render immediately
  const ability = buildAbilityFor('public', aclAbilities);
  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
};

UnifiedGuard.displayName = 'UnifiedGuard';

export default UnifiedGuard;
